import { useState, useRef, useEffect } from 'react';
import { HelpCircle, X, Send, Loader2, ChevronLeft, Plus, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactAdmin() {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | new | thread
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/my-threads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreads(res.data.threads);
    } catch (err) {
      console.error('Failed to load threads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && token) loadThreads();
  }, [open, token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeThread]);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, { subject: subject.trim(), message: message.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSent(true);
      setSubject('');
      setMessage('');
      setTimeout(() => { setSent(false); setView('list'); loadThreads(); }, 2000);
    } catch (err) {
      console.error('Send failed:', err);
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim() || sending || !activeThread) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/reply`, { thread_id: activeThread.thread_id, message: reply.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReply('');
      // Refresh thread
      const res = await axios.get(`${API}/messages/my-threads`, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.threads.find(t => t.thread_id === activeThread.thread_id);
      if (updated) setActiveThread(updated);
      setThreads(res.data.threads);
    } catch (err) {
      console.error('Reply failed:', err);
    } finally {
      setSending(false);
    }
  };

  const openThread = (thread) => {
    setActiveThread(thread);
    setView('thread');
  };

  const statusColor = (s) => {
    if (s === 'answered') return 'bg-green-100 text-green-700';
    if (s === 'closed') return 'bg-slate-100 text-slate-500';
    return 'bg-amber-100 text-amber-700';
  };

  if (!token || !user || user.is_restricted_demo) return null;
  // Hide for admin
  if (user?.is_admin) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="contact-admin-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 left-5 z-50 bg-slate-700 hover:bg-slate-800 text-white rounded-2xl p-3 shadow-lg transition-all hover:scale-105 group"
          title="Contact Admin"
        >
          <HelpCircle className="h-6 w-6" />
          <span className="absolute left-full ml-2 bg-slate-800 text-white text-xs px-2.5 py-1 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Contact Admin
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="contact-admin-panel"
          className="fixed bottom-24 left-5 z-50 w-[360px] h-[480px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-slate-700 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {view !== 'list' && (
                <button onClick={() => { setView('list'); setActiveThread(null); }} className="text-slate-300 hover:text-white">
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              <HelpCircle className="h-5 w-5 text-white" />
              <p className="text-white text-sm font-semibold">
                {view === 'new' ? 'New Message' : view === 'thread' ? activeThread?.subject : 'Admin Support'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {view === 'list' && (
                <button onClick={() => setView('new')} className="text-slate-300 hover:text-white p-1" title="New message">
                  <Plus className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => { setOpen(false); setView('list'); setActiveThread(null); }} className="text-slate-300 hover:text-white p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {/* Thread list */}
            {view === 'list' && (
              <div className="p-2">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : threads.length === 0 ? (
                  <div className="text-center py-10 px-4">
                    <HelpCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 font-medium">No messages yet</p>
                    <p className="text-xs text-slate-400 mt-1">Send a message to the admin for assistance with your account or analysis.</p>
                    <button
                      onClick={() => setView('new')}
                      className="mt-4 bg-slate-700 hover:bg-slate-800 text-white text-xs px-4 py-2 rounded-full transition-colors"
                    >
                      Send a Message
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {threads.map(t => (
                      <button
                        key={t.thread_id}
                        onClick={() => openThread(t)}
                        className="w-full text-left p-3 rounded-xl hover:bg-slate-50 transition-colors border border-slate-100"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-slate-700 truncate pr-2">{t.subject}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${statusColor(t.status)}`}>
                            {t.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          {t.messages[t.messages.length - 1]?.content.slice(0, 60)}...
                        </p>
                        <p className="text-[10px] text-slate-300 mt-1">
                          {new Date(t.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* New message form */}
            {view === 'new' && (
              <div className="p-4 space-y-3">
                {sent ? (
                  <div className="text-center py-10">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700">Message sent!</p>
                    <p className="text-xs text-slate-400 mt-1">The admin will respond shortly.</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Subject</label>
                      <input
                        data-testid="contact-subject-input"
                        type="text"
                        value={subject}
                        onChange={e => setSubject(e.target.value)}
                        placeholder="e.g. Need help with analysis"
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-slate-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Message</label>
                      <textarea
                        data-testid="contact-message-input"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder="Describe what you need help with..."
                        rows={6}
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-slate-400 resize-none"
                      />
                    </div>
                    <button
                      data-testid="contact-send-button"
                      onClick={handleSend}
                      disabled={!subject.trim() || !message.trim() || sending}
                      className="w-full bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white text-sm py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {sending ? 'Sending...' : 'Send Message'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Thread view */}
            {view === 'thread' && activeThread && (
              <div className="flex flex-col h-full">
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
                  {activeThread.messages.map((msg, i) => (
                    <div key={msg.id || i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                        msg.sender === 'user'
                          ? 'bg-slate-700 text-white rounded-br-md'
                          : 'bg-blue-50 text-slate-700 border border-blue-200 rounded-bl-md'
                      }`}>
                        <p className={`text-[10px] mb-0.5 ${msg.sender === 'user' ? 'text-slate-300' : 'text-blue-500'}`}>
                          {msg.sender === 'admin' ? 'Admin' : 'You'}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {activeThread.status !== 'closed' && (
                  <div className="p-3 border-t border-slate-200 shrink-0">
                    <div className="flex items-center gap-2">
                      <input
                        data-testid="contact-reply-input"
                        type="text"
                        value={reply}
                        onChange={e => setReply(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleReply()}
                        placeholder="Type a reply..."
                        disabled={sending}
                        className="flex-1 text-sm border border-slate-200 rounded-full px-4 py-2 focus:outline-none focus:border-slate-400"
                      />
                      <button
                        onClick={handleReply}
                        disabled={!reply.trim() || sending}
                        className="bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white rounded-full p-2 transition-colors"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                {activeThread.status === 'closed' && (
                  <div className="p-3 border-t text-center text-xs text-slate-400">This thread has been closed by admin.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
